import type { OnResetPassword, OnSendPasswordReset } from '#lib/auth-handlers'

import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@moment/design-system-ui/lib/cn'
import { type ChangeEvent, useCallback, useEffect, useState } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { z } from 'zod'

import { Button, ButtonVariant } from '#components/controls/button'
import { EmailInput } from '#components/controls/email'
import { ErrorText } from '#components/controls/input'
import { PasswordInput } from '#components/controls/password'
import { PasswordRequirements } from '#components/controls/password-requirements'
import { Text } from '#components/controls/text'
import {
  BackButton,
  CtaContainer,
  InputsContainer,
} from '#components/shared/styles'
import { PageMode } from '#lib/page-mode'

const sendResetSchema = z.object({
  email: z
    .string()
    .email({ message: 'Please enter valid email address' })
    .min(1, { message: 'Email is required' }),
})

const resetPasswordSchema = z
  .object({
    newPassword: z.string().min(1, { message: 'Password is required' }),
    confirmPassword: z.string(),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })

type SendResetInputs = z.infer<typeof sendResetSchema>
type ResetPasswordInputs = z.infer<typeof resetPasswordSchema>

interface PasswordResetProps {
  onSendPasswordReset: OnSendPasswordReset
  onResetPassword: OnResetPassword
  onBackToLogin: (withSuccess?: boolean) => void
  onModeChange?: (mode: PageMode) => void
  initialEmail?: string
  resetEmail?: string
  resetToken?: string
}

export const PasswordReset = ({
  onSendPasswordReset,
  onResetPassword,
  onBackToLogin,
  onModeChange,
  initialEmail,
  resetEmail,
  resetToken,
}: PasswordResetProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isEmailSent, setIsEmailSent] = useState(false)
  const [lastResetAttempt, setLastResetAttempt] = useState<number>(0)
  const [newPasswordValue, setNewPasswordValue] = useState('')
  const [passwordRequirementsMet, setPasswordRequirementsMet] = useState(false)
  const [passwordScore, setPasswordScore] = useState(0)

  const handleRequirementsChange = useCallback(
    (allMet: boolean, score: number) => {
      setPasswordRequirementsMet(allMet)
      setPasswordScore(score)
    },
    [],
  )

  // Determine which form to show
  const isResetMode = Boolean(resetToken)

  // Set the initial mode when the component mounts
  useEffect(() => {
    if (isResetMode) {
      onModeChange?.(PageMode.ResetPassword)
    } else {
      onModeChange?.(PageMode.ResetRequest)
    }
  }, [isResetMode, onModeChange])

  const schema = isResetMode ? resetPasswordSchema : sendResetSchema

  const {
    formState: { errors },
    handleSubmit,
    register,
    clearErrors,
    setError,
    watch,
  } = useForm({
    resolver: zodResolver(
      schema as z.ZodType<
        SendResetInputs | ResetPasswordInputs,
        z.ZodTypeDef & { typeName: string }
      >,
    ),
    defaultValues: isResetMode
      ? { newPassword: '', confirmPassword: '' }
      : { email: resetEmail || initialEmail || '' },
    mode: 'onSubmit',
  })

  const watchedEmail = watch('email')
  const emailIsValid = watchedEmail?.includes('@') ?? false

  const handleSendReset: SubmitHandler<SendResetInputs> = useCallback(
    async (formData) => {
      // Client-side rate limiting
      const RATE_LIMIT_DURATION_MS = 30_000
      const now = Date.now()
      const timeSinceLastAttempt = now - lastResetAttempt
      if (timeSinceLastAttempt < RATE_LIMIT_DURATION_MS) {
        const waitTimeSeconds = Math.ceil(RATE_LIMIT_DURATION_MS / 1000)
        setError('root.serverError', {
          message: `Please wait ${waitTimeSeconds} second${waitTimeSeconds > 1 ? 's' : ''} before requesting another reset email.`,
        })
        return
      }

      setIsSubmitting(true)
      setLastResetAttempt(now)

      try {
        const resetResponse = await onSendPasswordReset({
          email: formData.email,
          resetUrl: `${window.location.origin}/login`,
        })

        if (resetResponse.success) {
          setIsEmailSent(true)
          onModeChange?.(PageMode.ResetEmailSent)
        } else {
          const errorMessage =
            resetResponse.error || 'Failed to send reset email'
          setError('root.serverError', { message: errorMessage })
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error'
        setError('root.serverError', { message: errorMessage })
      } finally {
        setIsSubmitting(false)
      }
    },
    [setError, onModeChange, lastResetAttempt, onSendPasswordReset],
  )

  const handleResetPassword: SubmitHandler<ResetPasswordInputs> = useCallback(
    async (formData) => {
      // Double-check requirements are met and complexity score is sufficient
      if (!passwordRequirementsMet || passwordScore < 3) {
        setError('root.serverError', {
          message:
            'Please ensure all password requirements are met and password is strong enough',
        })
        return
      }

      setIsSubmitting(true)

      try {
        const resetResponse = await onResetPassword({
          newPassword: formData.newPassword,
          token: resetToken || '',
        })

        if (resetResponse.success) {
          // Go back to login with success flag
          onBackToLogin(true)
        } else {
          const errorMessage = resetResponse.error || 'Failed to reset password'
          setError('root.serverError', { message: errorMessage })
        }
      } catch (error: unknown) {
        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error'
        setError('root.serverError', { message: errorMessage })
      } finally {
        setIsSubmitting(false)
      }
    },
    [
      setError,
      resetToken,
      onBackToLogin,
      passwordRequirementsMet,
      passwordScore,
      onResetPassword,
    ],
  )

  const handleFormSubmit = useCallback(
    async (formData: SendResetInputs | ResetPasswordInputs) => {
      if (isResetMode) {
        await handleResetPassword(formData as ResetPasswordInputs)
      } else {
        await handleSendReset(formData as SendResetInputs)
      }
    },
    [isResetMode, handleResetPassword, handleSendReset],
  )

  // Handler to go back to the send reset link form
  const handleTryDifferentEmail = useCallback(() => {
    setIsEmailSent(false)
    onModeChange?.(PageMode.ResetRequest)
  }, [onModeChange])

  // Show success message after email is sent
  if (isEmailSent && !isResetMode) {
    return (
      <div className={cn('w-full', 'flex flex-col', '-mt-2.5')}>
        <div
          className={cn(
            'text-(--color-text-primary) dark:text-(--color-text-primary-contrast)',
            'max-w-85',
            'space-y-6',
          )}
        >
          <Text variant="md">
            If an account exists with this email address, you’ll receive a
            password reset email.
          </Text>
          <Text variant="md">Check your spam if you don’t see an email.</Text>
        </div>

        <CtaContainer>
          <Button
            label="Back to sign in"
            onClick={() => onBackToLogin(false)}
            variant={ButtonVariant.Secondary}
            className="w-full"
          />
        </CtaContainer>

        <BackButton
          onClick={handleTryDifferentEmail}
          data-testid="try-different-email-button"
          label="Try different email"
        />
      </div>
    )
  }

  return (
    <form
      className={cn('w-full', 'flex flex-col items-center')}
      onSubmit={handleSubmit(handleFormSubmit)}
      aria-label={isResetMode ? 'Reset password' : 'Send password reset link'}
      noValidate
    >
      <InputsContainer>
        {isResetMode ? (
          // Reset password form
          <>
            {/* Hidden email field for password managers */}
            {resetEmail && (
              <input
                type="text"
                name="email"
                value={resetEmail}
                autoComplete="username"
                style={{ display: 'none' }}
                readOnly
              />
            )}

            <PasswordInput
              label="New password"
              register={register('newPassword')}
              disabled={isSubmitting}
              onFocus={() => {
                clearErrors('newPassword')
              }}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                setNewPasswordValue(e.target.value)
                register('newPassword').onChange(e)
              }}
              autoComplete="new-password"
            />

            <PasswordInput
              label="Confirm password"
              register={register('confirmPassword')}
              error={errors.confirmPassword}
              disabled={isSubmitting}
              autoComplete="new-password"
            />

            <PasswordRequirements
              className={cn('-mt-0.5')}
              password={newPasswordValue}
              showRequirements={isResetMode}
              onRequirementsChange={handleRequirementsChange}
            />
          </>
        ) : (
          // Send reset email form
          <EmailInput
            register={register('email')}
            error={errors.email}
            disabled={isSubmitting}
            onFocus={() => {
              clearErrors('email')
            }}
          />
        )}
      </InputsContainer>

      <CtaContainer>
        <Button
          label={isResetMode ? 'Set password' : 'Send reset link'}
          type="submit"
          disabled={
            isResetMode
              ? isSubmitting || !passwordRequirementsMet
              : !emailIsValid || isSubmitting
          }
          isLoading={isSubmitting}
          variant={ButtonVariant.Primary}
          className="w-full"
        />

        {errors.root?.serverError && (
          <ErrorText
            error={errors.root.serverError.message}
            className="text-center w-full"
          />
        )}

        {!isResetMode && (
          <Text
            variant="sm"
            className={cn(
              'mt-2.5',
              'text-(--color-text-tertiary) dark:text-(--color-text-secondary-contrast)',
              'text-center max-w-56 mx-auto',
            )}
          >
            You’ll receive a password reset email if you have an account.
          </Text>
        )}
      </CtaContainer>

      <BackButton
        onClick={() => onBackToLogin(false)}
        data-testid="back-to-login-button"
        label="Back to sign in"
      />
    </form>
  )
}
